import React, { useMemo } from "react";
import PT from "prop-types";
import styled from "styled-components";
import ClockIcon from "_c/icon/Clock";
import CoffeeIcon from "_c/icon/Coffee";
import moment from "moment";
import { formatMinutesInterval } from "@/utils/time";
import isempty from "lodash/isEmpty";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { analyticsAction } from "@/utils/analytics";

const SettingsLink = styled(Link)`
  font-weight: 600;
  font-size: 14px;
  color: var(--font-color-emp);
  text-decoration: underline;
  margin-top: 10px;
  @media (max-width: 991px) {
    margin-left: auto;
    margin-right: auto;
  }
`;
const WeekdaysList = styled.ul`
  display: flex;
  flex-direction: column;
  width: 100%;
  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    margin: 0 10px;
    margin-right: 5px;
    margin-left: 0;
    border-bottom: ${({ theme }) => theme.border.light};
    padding: 5px 0;
  }
`;
const Placeholder = styled.div`
  width: 100%;
  min-height: 400px;
`;

const Weekday = styled.span`
  display: flex;
  font-size: 13px;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  font-weight: 700;
  width: 38px;
  height: 38px;
  color: white;
  min-width: 37px;
  min-height: 37px;
  border-radius: 50%;
  background: ${({ active, theme }) =>
    active ? theme.color.positive : theme.color.negative};
`;
const Hours = styled.div`
  display: flex;
  flex-direction: column;
  font-weight: 400;
  font-size: 14px;
  align-items: flex-end;
`;
const HoursVal = styled.div`
  display: flex;
  color: var(--font-color-accent);
  align-items: center;
  margin-bottom: 5px;
  > span {
    margin-inline-end: 7px;
  }
`;

const AsideSchedule = (props) => {
  const normalized = useMemo(() => {
    const schedule = props.weekSchedule;
    if (isempty(schedule)) return [];
    const localeData = moment.localeData(moment.locale());
    const weekdays = moment
      .weekdaysShort(true)
      .map((human) => ({ human, id: localeData.weekdaysParse(human) }));
    return weekdays.map(({ human, id }) => {
      const forDay = schedule[id];
      const { break: lunch, opening_hours } = forDay;
      const dayOff = opening_hours.every((m) => m === 0);
      const hasLunch = lunch.some(Boolean);
      return {
        weekday: human,
        lunch: hasLunch ? formatMinutesInterval(lunch[0], lunch[1]) : null,
        open: dayOff
          ? null
          : formatMinutesInterval(opening_hours[0], opening_hours[1]),
      };
    });
  }, [props.weekSchedule]);
  const onChangeScheduleClick = () => {
    if (props.isSaloon) {
      analyticsAction({
        category: "salon_profile",
        action: "salon_schedule_change",
      });
    } else {
      analyticsAction({
        category: "profile",
        action: "change_schedule_click",
      });
    }
  };
  return (
    <>
      <WeekdaysList>
        {normalized.map((forDay, idx) => {
          return (
            <li key={idx}>
              <Weekday active={forDay.open !== null}>{forDay.weekday}</Weekday>
              <Hours>
                <HoursVal>
                  <span>{forDay.open || props.t("common:day_off")}</span>
                  <ClockIcon color="var(--font-color-emp)" width="18" />
                </HoursVal>
                <HoursVal>
                  <span>
                    {forDay.lunch ? forDay.lunch : props.t("common:no_lunch")}
                  </span>
                  <CoffeeIcon color="var(--color-danger)" width="20" />
                </HoursVal>
              </Hours>
            </li>
          );
        })}
      </WeekdaysList>
      {props.isOwner && (
        <SettingsLink onClick={onChangeScheduleClick} to={props.scheduleLink}>
          {props.t("common:change_schedule")}
        </SettingsLink>
      )}
    </>
  );
};
AsideSchedule.propTypes = {
  isOwner: PT.bool.isRequired,
  weekSchedule: PT.array,
  scheduleLink: PT.string.isRequired,
  isSaloon: PT.bool,

  t: PT.func.isRequired,
};
const LazyAsideSchedule = (props) => {
  //return <AsideSchedule {...props} />;
  const { inView, ref } = useInView({
    triggerOnce: true,
    rootMargin: "250px",
  });
  if (inView) {
    return <AsideSchedule {...props} />;
  } else {
    return <Placeholder ref={ref} />;
  }
};
export default LazyAsideSchedule;
